import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import client, { fileUrl } from "../lib/api";
import { Crest, SectionTitle, Loading, Card, Pill, Zig } from "../components/common";
import { AdSlot } from "../components/AdSlot";
import { CLUB_HEADER, DEFAULT_HEADER } from "../lib/clubMedia";
import { CLUB_COLORS, DEFAULT_CLUB_COLOR } from "../lib/clubColors";

export default function ClubProfilePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [supporters, setSupporters] = useState([]);
  useEffect(() => { setData(null); client.get(`/clubs/${slug}`).then((r) => setData(r.data)).catch(() => setData(false)); }, [slug]);
  useEffect(() => { client.get(`/donations/wall?club=${slug}`).then((r) => setSupporters(r.data)).catch(() => {}); }, [slug]);
  if (data === null) return <Loading />;
  if (!data) return <div className="py-20 text-center">Club not found.</div>;
  const { club, squad, news, last_sheet } = data;
  const posOrder = { GK: 0, DEF: 1, MID: 2, FWD: 3 };
  return (
    <div data-testid="club-profile-page">
      <div className="relative rounded-sm overflow-hidden mb-6 min-h-[190px] flex items-end grain" data-testid="club-header"
        style={{ borderBottom: `4px solid ${CLUB_COLORS[club.slug] || DEFAULT_CLUB_COLOR}` }}>
        <img src={CLUB_HEADER[club.slug] || DEFAULT_HEADER} alt={`${club.name} supporters and stadium atmosphere`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${CLUB_COLORS[club.slug] || DEFAULT_CLUB_COLOR} 0%, ${CLUB_COLORS[club.slug] || DEFAULT_CLUB_COLOR}E6 42%, ${CLUB_COLORS[club.slug] || DEFAULT_CLUB_COLOR}66 100%)` }} />
        <div className="absolute inset-0 bg-black/15" />
        <div className="relative p-6 flex items-center gap-5 text-cream" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}>
          <Crest slug={club.slug} name={club.name} size={72} url={club.badge} />
          <div>
            <h1 className="font-head font-black text-3xl tracking-tight">{club.name}</h1>
            <p className="text-cream/90">{club.city} · Castle Lager PSL 2026</p>
          </div>
        </div>
      </div>

      {club.profile && (
        <Card className="p-5 mb-6" data-testid="club-profile-facts">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gold font-bold mb-3">Club profile</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <Fact label="Founded" value={club.profile.founded} />
            <Fact label="Home stadium" value={club.profile.stadium} />
            <Fact label="Location" value={club.profile.base} />
            <Fact label="Head coach" value={club.profile.coach} />
            <Fact label="Captain" value={club.profile.captain} />
          </div>
          {club.profile.note && <p className="text-xs text-ink/50 mt-4 border-t border-pslborder pt-3">{club.profile.note}</p>}
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <SectionTitle kicker="Squad">Players</SectionTitle>
            <Card className="divide-y divide-pslborder">
              {[...squad].sort((a, b) => posOrder[a.position] - posOrder[b.position] || a.shirt - b.shirt).map((p) => (
                <Link to={`/players/${p.id}`} key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream" data-testid={`squad-player-${p.id}`}>
                  <span className="w-7 text-center font-head font-bold text-ink/40 tabular">{p.shirt}</span>
                  {p.photo ? <img src={fileUrl(p.photo)} alt={p.name} className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-cream border border-pslborder" />}
                  <span className="flex-1 font-semibold text-sm">{p.name}</span>
                  <Pill tone="muted">{p.position}</Pill>
                  <Zig value={p.zig_price} className="text-sm font-bold text-green w-24 text-right" />
                </Link>
              ))}
            </Card>
          </div>
          {club.photos?.length > 0 && (
            <div>
              <SectionTitle kicker="Gallery">Club photos</SectionTitle>
              <div className="grid grid-cols-3 gap-2">
                {club.photos.map((ph, i) => <img key={i} src={fileUrl(ph)} alt={`${club.name} photo ${i + 1}`} className="rounded-sm aspect-square object-cover" />)}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-6">
          <div>
            <SectionTitle kicker="Team sheet">Last XI</SectionTitle>
            <Card className="p-4">
              {last_sheet ? (
                <>
                  <Pill tone="confirmed">Confirmed</Pill>
                  <ul className="mt-3 space-y-1 text-sm">
                    {last_sheet.starting_xi.map((pid) => { const p = squad.find((s) => s.id === pid); return <li key={pid} className="flex gap-2"><span className="text-ink/40 tabular w-5">{p?.shirt}</span>{p?.name || pid}</li>; })}
                  </ul>
                </>
              ) : <p className="text-sm text-ink/50">No confirmed sheet published yet.</p>}
            </Card>
          </div>
          <AdSlot id="community.club_sidebar" height={240} />
          <div>
            <SectionTitle kicker="Diaspora">Supporters wall</SectionTitle>
            <Card className="divide-y divide-pslborder max-h-96 overflow-y-auto scrollbar-thin" data-testid="supporters-wall">
              {supporters.length === 0 ? (
                <p className="p-4 text-sm text-ink/50">No public backers yet. Be the first to back {club.name} from the diaspora.</p>
              ) : supporters.map((s, i) => (
                <div key={i} className="p-3" data-testid={`supporter-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-green truncate">{s.donor_name}</span>
                    {typeof s.amount === "number" && <span className="text-xs text-gold font-bold tabular shrink-0">${s.amount.toFixed(2)}</span>}
                  </div>
                  {s.message && <p className="text-xs text-ink/60 mt-0.5">"{s.message}"</p>}
                </div>
              ))}
            </Card>
          </div>
          <div>
            <SectionTitle kicker="Club">Latest news</SectionTitle>
            <Card className="divide-y divide-pslborder">
              {news.map((s) => <Link key={s.id} to={`/news/${s.slug}`} className="block p-3 text-sm font-semibold hover:bg-cream hover:text-green">{s.title}</Link>)}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink/40 font-bold">{label}</div>
      <div className="text-sm font-semibold text-ink mt-0.5">{value}</div>
    </div>
  );
}
